import { lucideIcons, esc } from '../app.js';

const FAQ_ITEMS = [
  {
    q: 'What is BlindKeep?',
    a: `BlindKeep is an encrypted personal vault. You store files, passwords, and secrets — all encrypted in your browser before anything leaves your device. We never see your data.`
  },
  {
    q: 'What does "zero knowledge" mean?',
    a: `It means BlindKeep cannot read your data. Your master key is derived from your password on your device using Argon2id. We only receive an authentication key (a separate derivation) — never the encryption key. Even if our servers were seized, your data is indecipherable.`
  },
  {
    q: 'What encryption does BlindKeep use?',
    a: `<ul>
      <li><strong>XChaCha20-Poly1305</strong> — authenticated symmetric encryption for all items (256-bit keys, 24-byte nonces)</li>
      <li><strong>Argon2id</strong> — memory-hard password hashing for key derivation</li>
      <li><strong>HKDF-SHA256</strong> — deterministic subkey generation (auth key, encryption key, wrapping key)</li>
      <li><strong>X25519</strong> — elliptic-curve Diffie-Hellman for grant key exchange</li>
    </ul>
    <p>Each item gets its own random key, double-wrapped: the item is encrypted with its key, and the key is encrypted with your master-derived encryption key.</p>`
  },
  {
    q: 'Is BlindKeep post-quantum safe?',
    a: `<p><strong>Your stored data: yes.</strong> BlindKeep uses XChaCha20-Poly1305 with 256-bit keys for all item encryption. Grover's algorithm (the best known quantum attack on symmetric ciphers) halves the effective key strength to 128 bits — still considered secure by NIST and the broader cryptographic community.</p>
    <p><strong>Sharing and key exchange: not yet.</strong> Grant sharing uses X25519 (Curve25519 ECDH), which is vulnerable to Shor's algorithm on a sufficiently large quantum computer. The same applies to passkey signatures (Ed25519). No such computer exists today, and current estimates place that threat years to decades away.</p>
    <p><strong>What this means in practice:</strong></p>
    <ul>
      <li>Data at rest in your vault is quantum-resistant right now</li>
      <li>Shared grants use classical asymmetric crypto that will need upgrading when post-quantum key exchange (ML-KEM / CRYSTALS-Kyber) matures in browser APIs</li>
      <li>A "harvest now, decrypt later" attacker who captures your encrypted blobs gains nothing — the symmetric encryption is PQ-safe</li>
      <li>We are tracking NIST post-quantum standards and will migrate key exchange to ML-KEM when WebCrypto and WASM library support is production-ready</li>
    </ul>`
  },
  {
    q: 'Can BlindKeep employees read my data?',
    a: `No. Your encryption key never leaves your device. We store only encrypted blobs and an authentication key hash. There is no backdoor, admin panel, or key escrow that would let anyone at BlindKeep decrypt your data. The only metadata we see is file size (needed for billing).`
  },
  {
    q: 'What happens if BlindKeep shuts down?',
    a: `Your encrypted data is stored in S3-compatible object storage. We plan to offer data export so you can download your encrypted blobs and wrapped keys. With your password, you can always decrypt locally — the crypto is standard, open algorithms.`
  },
  {
    q: 'What are "drops"?',
    a: `Drops are anonymous, encrypted file shares. No account needed. You upload a file, it's encrypted in your browser, and you get a pickup link plus a 12-word passphrase. The file auto-deletes after 60 minutes. Share the link and passphrase separately for security.`
  },
  {
    q: 'How does the digital will work?',
    a: `You name an heir and select which vault items they should inherit. BlindKeep wraps your item keys so only the heir can decrypt them — and only after the dead man's switch triggers. The switch checks in with you on a schedule you set (weekly, monthly, quarterly). Miss enough check-ins and the will activates, emailing your heir a secure decryption link.`
  },
  {
    q: 'How does sharing (grants) work?',
    a: `You share a vault item by creating a grant. The item key is encrypted for the recipient using X25519 key exchange (if they have an account) or a link-secret embedded in the URL fragment (if they don't). You can set view limits, expiry dates, IP restrictions, and revoke access at any time. The server never sees the decryption key.`
  },
  {
    q: 'How much does it cost?',
    a: `<p>BlindKeep uses <strong>GB·year</strong> pricing — you pay for the storage you use over time. 1 GB·year means storing 1 GB for one year, or 2 GB for six months, or 12 GB for one month.</p>
    <ul>
      <li><strong>Drops are free</strong> — no account needed</li>
      <li><strong>Free tier included</strong> — enough for thousands of text secrets</li>
      <li><strong>&euro;5 per GB&middot;year</strong> — pay as you go, no subscriptions</li>
      <li><strong>&euro;25 per 10 GB&middot;years</strong> — 50% volume discount</li>
      <li>Unused balance never expires</li>
    </ul>
    <p><strong>Examples:</strong></p>
    <ul>
      <li>You store 500 MB of passwords and documents. A single &euro;5 top-up lasts <strong>2 years</strong>.</li>
      <li>You store 5 GB of family photos and legal files. A &euro;25 top-up (10 GB&middot;years) lasts <strong>2 years</strong>.</li>
    </ul>`
  },
  {
    q: 'Where is my data stored?',
    a: `Encrypted blobs are stored in European data centres (Scaleway, France). The server runs in the EU. Your data never leaves Europe — and even if it did, it's encrypted with keys only you hold.`
  },
  {
    q: 'What if I forget my password?',
    a: `If you set up a recovery key during registration, you can use it to regain access to your vault. Without a recovery key or a registered passkey with PRF, a forgotten password means your data is permanently inaccessible — that's the trade-off of true zero-knowledge encryption.`
  },
];

export function renderFaq(container) {
  container.innerHTML = `
    <div class="landing-header">
      <a class="landing-logo" href="#/drop" style="text-decoration:none;color:var(--text)">
        <svg viewBox="0 0 32 32" width="36" height="36" style="flex-shrink:0"><defs><linearGradient id="bk-faq" x1="0" y1="0" x2="1" y2="1"><stop offset="0%" stop-color="#3ecfb4"/><stop offset="100%" stop-color="#2a9d8f"/></linearGradient></defs><rect width="32" height="32" rx="7" fill="url(#bk-faq)"/><rect x="10" y="14" width="12" height="10" rx="2" fill="#0c0e14"/><path d="M12.5 14V11a3.5 3.5 0 0 1 7 0v3" fill="none" stroke="#0c0e14" stroke-width="2.2" stroke-linecap="round"/><circle cx="16" cy="19" r="1.5" fill="url(#bk-faq)"/><line x1="16" y1="20.5" x2="16" y2="22" stroke="url(#bk-faq)" stroke-width="1.5" stroke-linecap="round"/></svg>
        <div class="brand">Blind<span>Keep</span></div>
      </a>
      <a class="landing-signin" href="#/login">Sign in</a>
    </div>
    <div class="faq-body">
      <h1 class="faq-title">FAQ</h1>
      <div class="faq-list">
        ${FAQ_ITEMS.map((item, i) => `
          <details class="faq-item" ${i === 0 ? 'open' : ''}>
            <summary class="faq-question">
              <span>${esc(item.q)}</span>
              <i data-lucide="chevron-down" class="faq-chevron" style="width:18px;height:18px;flex-shrink:0"></i>
            </summary>
            <div class="faq-answer">${item.a}</div>
          </details>
        `).join('')}
      </div>
    </div>
    <div class="landing-footer"><a href="#/architecture" class="arch-footer-link">End-to-end encrypted</a> &middot; <a href="#/architecture" class="arch-footer-link">Zero knowledge</a> &middot; <a href="#/privacy" class="arch-footer-link">No tracking</a> &middot; <a href="#/compare" class="arch-footer-link">Compare</a></div>
    <div class="eu-badge">\u{1F1EA}\u{1F1FA} Made &amp; operated in the EU</div>
  `;
  lucideIcons();
}
